import axios from "axios";
import { getAccessTokenSilently } from "@/auth";

const BASE_URL = "https://billing.clafoutis.eu/api";

export const ISSUE_STATUSES = [
  { id: "todo", label: "À faire" },
  { id: "in_progress", label: "En cours" },
  { id: "done", label: "Terminé" },
  { id: "revenue_la", label: "Revenus LA" },
  { id: "revenue_br", label: "Revenue BR" },
  { id: "figures", label: "Les Chiffres" },
];

export const ISSUE_PRIORITIES = [
  { id: 0, label: "Aucune" },
  { id: 1, label: "Basse" },
  { id: 2, label: "Moyenne" },
  { id: 3, label: "Haute" },
  { id: 4, label: "Urgente" },
];

export function useBoardService() {

  async function client() {
    const token = await getAccessTokenSilently();
    return axios.create({
      baseURL: BASE_URL,
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  return {
    async listIssues(params = {}) {
      return (await (await client()).get("/issues", { params })).data;
    },
    async getIssue(id) {
      return (await (await client()).get(`/issues/${id}`)).data;
    },
    async createIssue(payload) {
      return (await (await client()).post("/issues", payload)).data;
    },
    async updateIssue(id, payload) {
      return (await (await client()).patch(`/issues/${id}`, payload)).data;
    },
    async deleteIssue(id) {
      return (await (await client()).delete(`/issues/${id}`)).data;
    },
    async mergeIssues(targetId, sourceIds) {
      return (await (await client()).post("/issues/merge", { target_id: targetId, source_ids: sourceIds })).data;
    },
    async addComment(id, body) {
      return (await (await client()).post(`/issues/${id}/comments`, { body })).data;
    },
    async updateComment(id, commentId, body) {
      return (await (await client()).patch(`/issues/${id}/comments/${commentId}`, { body })).data;
    },
    async deleteComment(id, commentId) {
      return (await (await client()).delete(`/issues/${id}/comments/${commentId}`)).data;
    },
    async addAttachment(id, file) {
      const form = new FormData();
      form.append("file", file);
      return (
        await (await client()).post(`/issues/${id}/attachments`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        })
      ).data;
    },
    async deleteAttachment(id, attachmentId) {
      return (await (await client()).delete(`/issues/${id}/attachments/${attachmentId}`)).data;
    },
    async linkTransaction(id, transactionId) {
      return (await (await client()).post(`/issues/${id}/transactions`, { transaction_id: transactionId })).data;
    },
    async unlinkTransaction(id, transactionId) {
      return (await (await client()).delete(`/issues/${id}/transactions/${transactionId}`)).data;
    },
    async linkInvoice(id, invoiceId) {
      return (await (await client()).post(`/issues/${id}/invoices`, { invoice_id: invoiceId })).data;
    },
    async unlinkInvoice(id, invoiceId) {
      return (await (await client()).delete(`/issues/${id}/invoices/${invoiceId}`)).data;
    },
    async listLabels() {
      return (await (await client()).get("/issues/labels")).data;
    },
    async createLabel(payload) {
      return (await (await client()).post("/issues/labels", payload)).data;
    },
    async attachLabel(id, labelId) {
      return (await (await client()).post(`/issues/${id}/labels`, { label_id: labelId })).data;
    },
    async detachLabel(id, labelId) {
      return (await (await client()).delete(`/issues/${id}/labels/${labelId}`)).data;
    },
    async listUsers() {
      return (await (await client()).get("/issues/users")).data;
    },
    async unreadCount() {
      return (await (await client()).get("/issues/unread-count")).data;
    },
    async searchTransactions(q) {
      return (await (await client()).get("/issues/search/transactions", { params: { q } })).data;
    },
    async searchInvoices(q) {
      return (await (await client()).get("/issues/search/invoices", { params: { q } })).data;
    },
    async searchCompanies(q) {
      return (await (await client()).get("/issues/search/companies", { params: { q } })).data;
    },
    async companyRelated(companyId) {
      return (await (await client()).get(`/issues/company/${companyId}/related`)).data;
    },
    async linkCompany(id, companyId) {
      return (await (await client()).post(`/issues/${id}/companies`, { company_id: companyId })).data;
    },
    async unlinkCompany(id, companyId) {
      return (await (await client()).delete(`/issues/${id}/companies/${companyId}`)).data;
    },
    async addMember(id, userId) {
      return (await (await client()).post(`/issues/${id}/members`, { user_id: userId })).data;
    },
    async removeMember(id, userId) {
      return (await (await client()).delete(`/issues/${id}/members/${userId}`)).data;
    },
    async addChecklist(id, title) {
      return (await (await client()).post(`/issues/${id}/checklists`, { title })).data;
    },
    async deleteChecklist(id, checklistId) {
      return (await (await client()).delete(`/issues/${id}/checklists/${checklistId}`)).data;
    },
    async addChecklistItem(id, checklistId, content) {
      return (await (await client()).post(`/issues/${id}/checklists/${checklistId}/items`, { content })).data;
    },
    async updateChecklistItem(id, checklistId, itemId, payload) {
      return (await (await client()).patch(`/issues/${id}/checklists/${checklistId}/items/${itemId}`, payload)).data;
    },
    async deleteChecklistItem(id, checklistId, itemId) {
      return (await (await client()).delete(`/issues/${id}/checklists/${checklistId}/items/${itemId}`)).data;
    },
  };
}
